import { useTranslation } from "react-i18next";
import { IconTrash } from "@tabler/icons-react";
import { useCurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { useApiQuery } from "../../../../services/api";
import { useActiveLang } from "../../../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../../../domains/room/hooks/useRoomName";
import { getRoomLogoUrl } from "../../../../domains/room/utils/getRoomLogoUrl";
import { pushSettingsView } from "../../../../domains/app/stores/settingsSheetStore";
import { Avatar } from "../../../ui/Avatar";
import Loader from "../../../Loader";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function RoomsView(): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();

  const roomsQuery = useApiQuery(
    "/webapp/private/rooms",
    currentUser === null ? false : {}
  );

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("My rooms")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        {/* `idle` is the disabled query — the current user is not loaded yet,
            so it reads as loading here too. */}
        {roomsQuery.state === "idle" || roomsQuery.state === "pending" ? (
          <Loader expanded />
        ) : roomsQuery.state === "error" ? (
          <p className="pt-4 text-center text-ui-note text-ds-slate-600">
            {roomsQuery.message}
          </p>
        ) : roomsQuery.body.rooms.length === 0 ? (
          <p className="pt-4 text-center text-ui-note text-ds-slate-600">
            {t("You haven't joined any rooms yet.")}
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-ds-slate-100">
            {roomsQuery.body.rooms.map((room) => {
              const roomName = getRoomName(room, activeLang);
              return (
                // A plain row, not a `SettingsRow`: the only action is
                // destructive, so the trailing button owns the tap rather than
                // the whole row.
                <div
                  key={room.id}
                  className="flex w-full items-center gap-3 px-1 py-2"
                >
                  <Avatar
                    variant="image"
                    size="md"
                    imageFit="scale-down"
                    src={getRoomLogoUrl(room)}
                    alt={roomName}
                    badge={null}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0">
                    <span className="truncate text-ui-body font-semibold text-ds-black">
                      {roomName}
                    </span>
                    <span className="truncate text-footnote text-ds-slate-700">
                      {t("by {{name}}", { name: room.organisationModel.name })}
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label={t("Leave Room")}
                    onClick={() => {
                      pushSettingsView({
                        view: "leaveRoom",
                        roomId: room.id,
                        roomName,
                      });
                    }}
                    className="flex size-8 shrink-0 items-center justify-center text-ds-clementine-600"
                  >
                    <IconTrash size={20} stroke={1.5} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
